import { useMemo } from 'react';
import { CourtPadTab } from './CourtPadTab';

export interface ClubAmericanoSingleCourtPadProps {
  /** The active club-format (Klubb A vs Klubb B) tournament. */
  tournament: any;
  /** The single court whose pad is rendered. */
  court: any;
  /** Records a result from the court pad. */
  onRecordResult?: any;
  /** Submission phase for the match being sent — forwarded to <CourtPadTab>. */
  submitStatus?: { phase: 'idle' | 'sending' | 'done'; matchId: string | null };
  /** Persists in-progress points without finalising. */
  onLiveScore?: any;
  /** Called when a player name is clicked. */
  onPlayerClick?: any;
  /**
   * Adapts an Americano match shape into the slot-based shape CourtPadTab expects.
   * Helper that the call-site must pass.
   */
  americanoMatchToSlots: (m: any) => any;
  /**
   * The live `Padel24Engine` wiring object.
   * Forwarded to <CourtPadTab> → <ClubPadBanner>.
   */
  Padel24Engine: any;
  /**
   * Plays an end-sound by key — shared audio helper (Web Audio).
   * NOTE: the call-site must pass it.
   */
  playEndSound: (soundType?: string) => void;
  /**
   * Live clock from the parent — forwarded to <CourtPadTab> so the pad's match
   * feed runs on the SAME clock that resolved this session onto the court.
   * Omitted → CourtPadTab ticks its own.
   */
  now?: any;
  /** Projected match numbers + club scoreboard — forwarded to <CourtPadTab>. */
  globalMatchNumber?: Record<string, number> | null;
  clubStandings?: { standings: any; leader: any } | null;
  /** Device kiosk mode: forwarded to <CourtPadTab> → <CourtPadCard> to drop size caps. */
  fill?: boolean;
}

// Club variant of the Americano pad — Klubb A vs Klubb B with the clubs' main score
// at the top (totalScore + V/T/U), and the regular court pad below.
/**
 * Single-court club-format banepad — same slot-adapted <CourtPadTab onlyCourt> as the
 * Americano pad, wrapped in a full-width div so the club score banner pins above it.
 * Extracted verbatim from the original artifact (legacy/App.jsx); markup/logic unchanged.
 * Takes `americanoMatchToSlots`, `Padel24Engine` and `playEndSound` as props.
 */
export function ClubAmericanoSingleCourtPad({
  tournament,
  court,
  onRecordResult,
  submitStatus,
  onLiveScore,
  onPlayerClick,
  now,
  globalMatchNumber = null,
  clubStandings = null,
  americanoMatchToSlots,
  Padel24Engine,
  playEndSound,
  fill = false,
}: ClubAmericanoSingleCourtPadProps) {
  // deviation: original deps are [tournament] only (americanoMatchToSlots was a
  // stable module-scope fn); preserved verbatim.
  const adapted = useMemo(
    () => ({ ...tournament, matches: (tournament.matches || []).map(americanoMatchToSlots) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tournament],
  );
  return (
    <div className="w-full">
      <CourtPadTab
        tournament={adapted}
        now={now}
        globalMatchNumber={globalMatchNumber}
        clubStandings={clubStandings}
        onlyCourt={court}
        onRecordResult={onRecordResult}
        submitStatus={submitStatus}
        onLiveScore={onLiveScore}
        onPlayerClick={onPlayerClick}
        Padel24Engine={Padel24Engine}
        playEndSound={playEndSound}
        fill={fill}
      />
    </div>
  );
}
